// Intern state
let mapState = {
    map: null,
    maps: null,
    type: null,
    scenarioId: null,
    turfPolygons: {},
    turfIndex: {},          // Map<layerType, Map<featureId, turfEntry>> for O(1) lookup
    layerFeatureIds: {},    // Map<layerType, string[]> for scoped style application
    featureCollections: {}, // Cache of original GeoJSON FeatureCollections per layer type
    layersOnMap: new Set(), // Which layers currently have their features on map.data
    history: [],
    previousSelectedAreaIds: new Set(),
    previousSelectionType: null,
    disableFitBounds: false,
    tools: null,
    popup: null,
    salesDistricts: null,
    selectionType: 'clear',
    onAreaClick: null,
    styleUpdateFrame: null,
    areaClickEnabled: true
};


/**
 * Hämta hela map state
 * @returns {Object} Map state
 */
export function getMapState() {
    return mapState;
}

/**
 * Initiera map när Google Maps API är laddad
 * @param {Object} params - { map, maps }
 * @throws {Error} If map or maps is null
 */
export function initMapState({ map, maps }) {
    if (!map || !maps) {
        const error = new Error('map-state: Map or maps is null in initMapState');
        console.error(error.message);
        throw error;
    }

    mapState.map = map;
    mapState.maps = maps;
    mapState.previousSelectedAreaIds = new Set();
    mapState.previousSelectionType = null;
}

/**
 * Kontrollera om map är initialiserad
 * @returns {boolean} True om map och maps är initialiserade
 */
export function isInitialized() {
    return mapState.map !== null && mapState.maps !== null;
}

/**
 * Hämta map-instans
 * @returns {Object|null} Google Maps map instance
 */
export function getMap() {
    return mapState.map;
}

/**
 * Hämta maps API-instans
 * @returns {Object|null} Google Maps API instance
 */
export function getMaps() {
    return mapState.maps;
}

/**
 * Sätt type och scenarioId
 * @param {string} type - District type
 * @param {string} scenarioId - Scenario ID
 */
export function setTypeAndScenario(type, scenarioId) {
    mapState.type = type;
    mapState.scenarioId = scenarioId;
}


/**
 * Hämta selection type
 * @returns {string} Selection type
 */
export function getSelectionType() {
    return mapState.selectionType;
}

/**
 * Sätt selection type och notifiera callbacks
 * @param {string} type - Selection type
 */
export function setSelectionType(type) {
    mapState.selectionType = type;
    
    // Återställ previousSelectedAreaIds när selection type ändras
    mapState.previousSelectedAreaIds = new Set();
}


/**
 * Sätt turf polygons
 * @param {Object} turfPolygons - Object med layer types som keys
 */
export function setTurfPolygons(turfPolygons) {
    mapState.turfPolygons = turfPolygons;
}

/**
 * Uppdatera turf polygons (merge med existing).
 * Builds a Map<featureId, turfEntry> index per layer for O(1) click lookups,
 * and a string[] of feature IDs per layer for scoped style operations.
 * @param {Object} newTurfPolygons - Object med layer types som keys
 */
export function updateTurfPolygons(newTurfPolygons) {
    mapState.turfPolygons = {
        ...mapState.turfPolygons,
        ...newTurfPolygons
    };

    for (const layerType of Object.keys(newTurfPolygons)) {
        const entries = newTurfPolygons[layerType];
        const indexMap = new Map();
        const idList = [];
        for (const entry of entries) {
            const id = entry.feature.properties.id;
            indexMap.set(id, entry);
            idList.push(id);
        }
        mapState.turfIndex[layerType] = indexMap;
        mapState.layerFeatureIds[layerType] = idList;
    }
}

/**
 * Hämta turf polygons för en specifik type
 * @param {string} type - Layer type
 * @returns {Array|null} Turf polygons för type eller null
 */
export function getTurfPolygons(type) {
    return mapState.turfPolygons[type] || null;
}

/**
 * Hämta alla turf polygons
 * @returns {Object} Object med layer types som keys
 */
export function getAllTurfPolygons() {
    return mapState.turfPolygons;
}

/**
 * O(1) lookup of a turfEntry by featureId within a given layer type.
 * @param {string} layerType - Layer type
 * @param {string} featureId - Feature ID to look up
 * @returns {Object|undefined} The turfEntry or undefined
 */
export function getTurfEntryById(layerType, featureId) {
    return mapState.turfIndex[layerType]?.get(featureId);
}

/**
 * Get the list of feature IDs belonging to a layer type.
 * @param {string} layerType - Layer type
 * @returns {string[]|null} Array of feature IDs or null
 */
export function getLayerFeatureIds(layerType) {
    return mapState.layerFeatureIds[layerType] || null;
}

/**
 * Kontrollera om en layer type är laddad
 * @param {string} type - Layer type
 * @returns {boolean} True om layer är laddad
 */
export function hasLayerType(type) {
    return mapState.turfPolygons[type] !== undefined;
}

export function setFeatureCollection(type, featureCollection) {
    mapState.featureCollections[type] = featureCollection;
}

export function getFeatureCollection(type) {
    return mapState.featureCollections[type] || null;
}

export function setLayerOnMap(type) {
    mapState.layersOnMap.add(type);
}

export function removeLayerFromMap(type) {
    mapState.layersOnMap.delete(type);
}

export function isLayerOnMap(type) {
    return mapState.layersOnMap.has(type);
}

/**
 * Sätt tools
 * @param {Object} tools - Tools instance
 */
export function setTools(tools) {
    mapState.tools = tools;
}

/**
 * Sätt popup
 * @param {Object} popup - Popup instance
 */
export function setPopup(popup) {
    mapState.popup = popup;
}

/**
 * Sätt sales districts
 * @param {Object} districts - Sales districts FeatureCollection
 */
export function setSalesDistricts(districts) {
    mapState.salesDistricts = districts;
}

/**
 * Hämta sales districts
 * @returns {Object|null} Sales districts FeatureCollection
 */
export function getSalesDistricts() {
    return mapState.salesDistricts;
}

/**
 * Sätt disable fit bounds
 * @param {boolean} disable - Om fit bounds ska vara disabled
 */
export function setDisableFitBounds(disable) {
    mapState.disableFitBounds = disable;
}

/**
 * Sätt area click callback
 * @param {Function} callback - Callback för när area klickas
 */
export function setOnAreaClick(callback) {
    mapState.onAreaClick = callback;
}

/**
 * Hämta area click callback
 * @returns {Function|null} Area click callback
 */
export function getOnAreaClick() {
    return mapState.onAreaClick;
}

export function setAreaClickEnabled(enabled) {
    mapState.areaClickEnabled = enabled;
}

export function isAreaClickEnabled() {
    return mapState.areaClickEnabled;
}

/**
 * Hämta history
 * @returns {Array} History array
 */
export function getHistory() {
    return mapState.history;
}

/**
 * Lägg till i history
 * @param {Object} historyEntry - History entry
 */
export function addHistory(historyEntry) {
    mapState.history.push(historyEntry);
}

/**
 * Undo (gå tillbaka i history)
 * @returns {boolean} True om undo lyckades
 * @deprecated History funktionalitet är inte längre implementerad eftersom selectedAreas nu ligger i districtCache
 */
export function undo() {
    // TODO: Implementera undo med districtCache om det behövs
    return false;
}

/**
 * Hämta previous selected area IDs (för optimering)
 * @returns {Set} Set med previous selected area IDs
 */
export function getPreviousSelectedAreaIds() {
    return mapState.previousSelectedAreaIds;
}

/**
 * Sätt previous selected area IDs
 * @param {Set} ids - Set med previous selected area IDs
 */
export function setPreviousSelectedAreaIds(ids) {
    mapState.previousSelectedAreaIds = ids;
}

/**
 * Hämta previous selection type (för optimering)
 * @returns {string|null} Previous selection type
 */
export function getPreviousSelectionType() {
    return mapState.previousSelectionType;
}

/**
 * Sätt previous selection type
 * @param {string} type - Selection type
 */
export function setPreviousSelectionType(type) {
    mapState.previousSelectionType = type;
}

/**
 * Hämta style update frame (för requestAnimationFrame)
 * @returns {number|null} Style update frame ID
 */
export function getStyleUpdateFrame() {
    return mapState.styleUpdateFrame;
}

/**
 * Sätt style update frame
 * @param {number|null} frameId - Style update frame ID
 */
export function setStyleUpdateFrame(frameId) {
    mapState.styleUpdateFrame = frameId;
}
